import { Organization } from "../Entities/Organization";

export interface MissingProfileItem {
  key: string;
  label: string;
  route: string;
  hash?: string;
  tab?: string;
}

export interface ProfileCompletionResult {
  percentage: number;
  missingItems: MissingProfileItem[];
}

/**
 * Check if a value is considered "filled"
 */
const isFilled = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "boolean") return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    // For theme object, check if it's not just default
    if (value.themeName) {
      return value.themeName !== "default" || 
             (value.primaryColor && value.primaryColor !== "#001529");
    }
    return Object.keys(value).length > 0;
  }
  return true;
};

/**
 * Calculate profile completion percentage and get missing items
 */
export const calculateProfileCompletion = (
  org: Organization | null | undefined
): ProfileCompletionResult => {
  if (!org) {
    return {
      percentage: 0,
      missingItems: [],
    };
  }

  const allItems: Array<{
    key: string;
    label: string;
    route: string;
    hash?: string;
    tab?: string;
    check: () => boolean;
  }> = [
    {
      key: "orgName",
      label: "Organization Name",
      route: "/organization",
      tab: "general",
      hash: "#orgName",
      check: () => isFilled(org.orgName),
    },
    {
      key: "orgWebsite",
      label: "Organization Website",
      route: "/organization",
      tab: "general",
      hash: "#orgWebsite",
      check: () => isFilled(org.orgWebsite),
    },
    {
      key: "gst",
      label: "GST",
      route: "/organization",
      tab: "general",
      hash: "#gst",
      check: () => isFilled(org.gstNumber),
    },
    {
      key: "orgDomain",
      label: "Organization Domain",
      route: "/organization",
      tab: "general",
      hash: "#domain",
      check: () => isFilled(org.orgDomain),
    },
    {
      key: "timeZone",
      label: "Time Zone",
      route: "/organization",
      tab: "general",
      hash: "#timeZone",
      check: () => isFilled(org.timeZone),
    },
    {
      key: "currency",
      label: "Currency",
      route: "/organization",
      tab: "general",
      hash: "#currency",
      check: () => isFilled(org.currency),
    },
    {
      key: "supportEmail",
      label: "Support Email",
      route: "/organization",
      tab: "general",
      hash: "#supportEmail",
      check: () => isFilled(org.supportEmail),
    },
    {
      key: "industry",
      label: "Industry",
      route: "/organization",
      tab: "general",
      hash: "#industry",
      check: () => isFilled(org.industry),
    },
    {
      key: "description",
      label: "Description",
      route: "/organization",
      tab: "general",
      hash: "#description",
      check: () => isFilled(org.description),
    },
    {
      key: "logo",
      label: "Logo",
      route: "/organization",
      tab: "general",
      hash: "#logo",
      check: () => isFilled(org.logo),
    },
    {
      key: "theme",
      label: "Theme",
      route: "/organization",
      tab: "theme",
      check: () => {
        if (!org.theme) return false;
        return org.theme.themeName !== "default" || 
               (!!org.theme.primaryColor && org.theme.primaryColor !== "#001529");
      },
    },
  ];

  const completedCount = allItems.filter((item) => item.check()).length;
  const percentage = Math.round((completedCount / allItems.length) * 100);
  const missingItems = allItems
    .filter((item) => !item.check())
    .map(({ check, ...rest }) => rest);

  return {
    percentage,
    missingItems,
  };
};
